import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Edit2, GripVertical, Copy } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { masterdataApi } from '@/lib/courses';

type PackageDefinition = {
  id: string;
  name: string;
  units: number;
  features: string[];
  highlighted: boolean;
  highlightLabel: string | null;
  special: boolean;
  basePriceCents: number;
  immediateDiscountPercent: number | null;
  installmentEnabled: boolean;
  installmentMonths: number | null;
  signupFeeMode: 'always' | 'never' | 'first_abo';
  signupFeeIncluded: boolean;
  defaultPaymentType: 'immediate' | 'installment';
  // Alte Felder für Rückwärtskompatibilität
  immediatePriceMode?: 'total' | 'per_unit';
  immediatePriceCents?: number;
  installmentPriceMode?: 'total' | 'per_unit';
  installmentPriceCents?: number;
  // Berechnete Felder (vom Backend)
  totalCentsImmediate?: number;
  pricePerUnitCentsImmediate?: number;
  totalCentsInstallment?: number;
  pricePerUnitCentsInstallment?: number;
};

// Sofort- und Ratenpreis leitet das Backend aus Basispreis und Rabatt ab — aber nur,
// wenn kein fertiger Preis mitkommt. Blieb der alte beim Bearbeiten stehen, galt
// ein geänderter Basispreis nie: 99 € gepflegt, 81 € angezeigt (T-0250). Verworfen
// wird er nur, wenn Basispreis oder Rabatt geändert wurden: Viele Pakete aus dem
// Altsystem tragen einen Sofortrabatt nur im Sofortpreis, ohne Prozentsatz (Chris,
// 28.10.2026) — ein Speichern nur des Namens hätte sie sonst teurer gemacht.
const ABGELEITETE_PREISE = [
  'immediatePriceCents',
  'immediate_price_cents',
  'totalCentsImmediate',
  'pricePerUnitCentsImmediate',
  'installmentPriceCents',
  'installment_price_cents',
  'totalCentsInstallment',
  'pricePerUnitCentsInstallment',
] as const;

const ohneAbgeleitetePreise = (pkg: PackageDefinition): PackageDefinition => {
  const kopie: Record<string, unknown> = { ...pkg };
  for (const feld of ABGELEITETE_PREISE) delete kopie[feld];
  return kopie as PackageDefinition;
};

const basisVon = (pkg: PackageDefinition) => pkg.basePriceCents ?? (pkg.totalCentsImmediate ?? pkg.immediatePriceCents ?? 0);
const rabattVon = (pkg: PackageDefinition) => pkg.immediateDiscountPercent ?? null;

/** Gespeicherter Sofortpreis, wenn er nicht zu Basis − Rabatt passt (sonst null). */
const abweichenderSofortpreis = (pkg: PackageDefinition): number | null => {
  const gespeichert = pkg.totalCentsImmediate ?? pkg.immediatePriceCents;
  if (gespeichert === undefined || gespeichert === null) return null;
  const erwartet = Math.round(basisVon(pkg) * (1 - (rabattVon(pkg) ?? 0) / 100));
  return gespeichert === erwartet ? null : gespeichert;
};

interface Props {
  unterrichtsformId: number;
  onClose?: () => void;
}

const SortablePackageRow = ({
  pkg,
  onEdit,
  onDelete,
}: {
  pkg: PackageDefinition;
  onEdit: () => void;
  onDelete: () => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: pkg.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const formatPrice = (cents: number) => {
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  };

  return (
    <tr ref={setNodeRef} style={style} className="border-b">
      <td className="py-3 pr-4">
        <div className="flex items-center gap-2">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
          >
            <GripVertical className="h-4 w-4" />
          </div>
          <div className="font-medium">{pkg.name}</div>
          {pkg.highlighted && (
            <span className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary">
              {pkg.highlightLabel || 'meistgebucht'}
            </span>
          )}
        </div>
      </td>
      <td className="py-3 px-4">{pkg.units}</td>
      <td className="py-3 px-4">
        <div className="text-sm">
          <div>
            Basis: {formatPrice(pkg.basePriceCents ?? (pkg.totalCentsImmediate ?? pkg.immediatePriceCents ?? 0))}
            {pkg.immediateDiscountPercent && pkg.immediateDiscountPercent > 0 && (
              <span className="text-xs text-muted-foreground ml-1">
                ({pkg.immediateDiscountPercent}% Rabatt)
              </span>
            )}
          </div>
          <div className="text-muted-foreground">
            Sofort: {formatPrice(pkg.totalCentsImmediate ?? (pkg.basePriceCents ? Math.round(pkg.basePriceCents * (1 - (pkg.immediateDiscountPercent ?? 0) / 100)) : (pkg.immediatePriceCents ?? 0)))}
          </div>
          {(pkg.installmentEnabled ?? (pkg.installmentMonths && pkg.installmentMonths > 0)) && (
            <div className="text-muted-foreground">
              Raten: {formatPrice(pkg.totalCentsInstallment ?? pkg.basePriceCents ?? (pkg.installmentPriceCents ?? 0))}
              {pkg.installmentMonths && pkg.installmentMonths > 0 && (
                <span className="text-xs ml-1">
                  ({formatPrice(Math.round((pkg.totalCentsInstallment ?? pkg.basePriceCents ?? (pkg.installmentPriceCents ?? 0)) / pkg.installmentMonths))} pro Monat)
                </span>
              )}
            </div>
          )}
        </div>
      </td>
      <td className="py-3 px-4">
        {(pkg.installmentEnabled ?? (pkg.installmentMonths && pkg.installmentMonths > 0)) ? (pkg.installmentMonths || '—') : '—'}
        {pkg.installmentMonths && pkg.installmentMonths > 0 && (
          <div className="text-xs text-muted-foreground mt-1">
            {formatPrice(Math.round((pkg.totalCentsInstallment ?? pkg.basePriceCents ?? (pkg.installmentPriceCents ?? 0)) / pkg.installmentMonths))} pro Monat
          </div>
        )}
      </td>
      <td className="py-3 px-4">
        <div className="text-xs">
          {pkg.signupFeeMode === 'always' && <span className="text-primary">Immer</span>}
          {pkg.signupFeeMode === 'never' && <span className="text-muted-foreground">Nie</span>}
          {pkg.signupFeeMode === 'first_abo' && <span className="text-orange-600">Erstes Abo</span>}
          <div className="mt-1">
            {pkg.signupFeeIncluded ? (
              <span className="text-emerald-600">Preis inkl. Anmeldegebühr</span>
            ) : (
              <span className="text-muted-foreground">Preis exkl. Anmeldegebühr</span>
            )}
          </div>
        </div>
      </td>
      <td className="py-3 px-4">
        <div className="flex flex-col gap-1">
          {pkg.features.length > 0 && (
            <div className="text-xs text-muted-foreground">
              {pkg.features.length} Feature{pkg.features.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </td>
      <td className="py-3 px-4">
        {pkg.highlighted ? (
          <span className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary">
            {pkg.highlightLabel || 'meistgebucht'}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>
      <td className="py-3 pl-4">
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onEdit}>
            <Edit2 className="mr-1 h-3 w-3" />
            Bearbeiten
          </Button>
          <Button variant="destructive" size="sm" onClick={onDelete}>
            <Trash2 className="mr-1 h-3 w-3" />
            Löschen
          </Button>
        </div>
      </td>
    </tr>
  );
};

export const PackageManagementForm: React.FC<Props> = ({ unterrichtsformId, onClose }) => {
  const [packages, setPackages] = useState<PackageDefinition[]>([]);
  const [name, setName] = useState('');
  const [units, setUnits] = useState('');
  const [features, setFeatures] = useState('');
  const [highlighted, setHighlighted] = useState(false);
  const [highlightLabel, setHighlightLabel] = useState('');
  const [special, setSpecial] = useState(false);
  const [basePriceCents, setBasePriceCents] = useState('');
  const [immediateDiscountPercent, setImmediateDiscountPercent] = useState('');
  const [installmentEnabled, setInstallmentEnabled] = useState(false);
  const [installmentMonths, setInstallmentMonths] = useState('');
  const [defaultPaymentType, setDefaultPaymentType] = useState<'immediate' | 'installment'>('immediate');
  const [signupFeeMode, setSignupFeeMode] = useState<'always' | 'never' | 'first_abo'>('never');
  const [signupFeeIncluded, setSignupFeeIncluded] = useState(false);

  // Modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<PackageDefinition | null>(null);
  const [editName, setEditName] = useState('');
  const [editUnits, setEditUnits] = useState('');
  const [editFeatures, setEditFeatures] = useState('');
  const [editHighlighted, setEditHighlighted] = useState(false);
  const [editHighlightLabel, setEditHighlightLabel] = useState('');
  const [editSpecial, setEditSpecial] = useState(false);
  const [editBasePriceCents, setEditBasePriceCents] = useState('');
  const [editImmediateDiscountPercent, setEditImmediateDiscountPercent] = useState('');
  const [editInstallmentEnabled, setEditInstallmentEnabled] = useState(false);
  const [editInstallmentMonths, setEditInstallmentMonths] = useState('');
  const [editDefaultPaymentType, setEditDefaultPaymentType] = useState<'immediate' | 'installment'>('immediate');
  const [editSignupFeeMode, setEditSignupFeeMode] = useState<'always' | 'never' | 'first_abo'>('never');
  const [editSignupFeeIncluded, setEditSignupFeeIncluded] = useState(false);

  // Merge-Dialog
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [sourceUnterrichtsformId, setSourceUnterrichtsformId] = useState<string>('');
  const [availableForms, setAvailableForms] = useState<Array<{ id: number; name: string }>>([]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load packages
  useEffect(() => {
    if (unterrichtsformId > 0) {
      loadPackages();
      loadAvailableForms();
    }
  }, [unterrichtsformId]);

  const loadPackages = async () => {
    try {
      const data = await api.get<PackageDefinition[]>(`/api/unterrichtsformen/${unterrichtsformId}/packages`);
      setPackages(data || []);
    } catch (e: any) {
      toast.error(e?.message || 'Pakete konnten nicht geladen werden');
    }
  };

  const loadAvailableForms = async () => {
    try {
      const forms = await masterdataApi.unterrichtsformen();
      setAvailableForms((forms || []).filter(f => f.id !== unterrichtsformId));
    } catch (e: any) {
      console.error('Fehler beim Laden der Unterrichtsformen:', e);
    }
  };

  const resetForm = () => {
    setName('');
    setUnits('');
    setFeatures('');
    setHighlighted(false);
    setHighlightLabel('');
    setSpecial(false);
    setBasePriceCents('');
    setImmediateDiscountPercent('');
    setInstallmentEnabled(false);
    setInstallmentMonths('');
    setDefaultPaymentType('immediate');
    setSignupFeeMode('never');
    setSignupFeeIncluded(false);
  };

  const resetEditForm = () => {
    setEditName('');
    setEditUnits('');
    setEditFeatures('');
    setEditHighlighted(false);
    setEditHighlightLabel('');
    setEditSpecial(false);
    setEditBasePriceCents('');
    setEditImmediateDiscountPercent('');
    setEditInstallmentEnabled(false);
    setEditInstallmentMonths('');
    setEditDefaultPaymentType('immediate');
    setEditSignupFeeMode('never');
    setEditSignupFeeIncluded(false);
    setEditingPackage(null);
  };

  const savePackages = async (updatedPackages: PackageDefinition[]) => {
    try {
      await api.put(`/api/unterrichtsformen/${unterrichtsformId}/packages`, { packages: updatedPackages });
      return true;
    } catch (e: any) {
      toast.error(e?.message || 'Speichern fehlgeschlagen');
      return false;
    }
  };


  const handleAdd = async () => {
    if (!name.trim()) {
      toast.error('Bitte Paketname angeben');
      return;
    }
    const unitsNum = Number(units);
    if (!Number.isFinite(unitsNum) || unitsNum <= 0) {
      toast.error('Anzahl Unterrichtseinheiten muss > 0 sein');
      return;
    }

    const basePriceNum = Number(basePriceCents.replace(',', '.')) * 100;
    if (!Number.isFinite(basePriceNum) || basePriceNum <= 0) {
      toast.error('Basispreis muss > 0 sein');
      return;
    }

    const discountPercentNum = immediateDiscountPercent.trim() !== '' ? Number(immediateDiscountPercent.replace(',', '.')) : null;
    if (discountPercentNum !== null && (!Number.isFinite(discountPercentNum) || discountPercentNum < 0 || discountPercentNum > 100)) {
      toast.error('Sofortzahlungsrabatt muss zwischen 0 und 100% sein');
      return;
    }

    const parsedFeatures = features
      .split('\n')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    const installmentMonthsNum = installmentEnabled && installmentMonths.trim() !== '' ? Number(installmentMonths) : null;
    if (installmentEnabled && (installmentMonthsNum === null || !Number.isFinite(installmentMonthsNum) || installmentMonthsNum <= 0)) {
      toast.error('Bei aktivierter Ratenzahlung muss eine Ratenanzahl angegeben werden');
      return;
    }

    // Wenn highlighted aktiviert wird, alle anderen deaktivieren
    let updatedPackages = [...packages];
    if (highlighted) {
      updatedPackages = updatedPackages.map((p) => ({ ...p, highlighted: false }));
    }

    const next: PackageDefinition = {
      id: `${Date.now()}`,
      name: name.trim(),
      units: unitsNum,
      features: parsedFeatures,
      highlighted: highlighted,
      highlightLabel: highlightLabel.trim() || null,
      special: special,
      basePriceCents: Math.round(basePriceNum),
      immediateDiscountPercent: discountPercentNum,
      installmentEnabled: installmentEnabled,
      installmentMonths: installmentMonthsNum,
      defaultPaymentType: installmentEnabled && installmentMonthsNum ? defaultPaymentType : 'immediate',
      signupFeeMode: signupFeeMode,
      signupFeeIncluded,
    };

    const nextList = [...updatedPackages, next];
    setPackages(nextList);
    const success = await savePackages(nextList);
    if (success) {
      toast.success('Paket hinzugefügt');
      resetForm();
    }
  };

  const handleDelete = async (id: string) => {
    const next = packages.filter((p) => p.id !== id);
    setPackages(next);
    const success = await savePackages(next);
    if (success) {
      toast.info('Paket entfernt');
    }
  };

  const handleEditClick = (pkg: PackageDefinition) => {
    setEditingPackage(pkg);
    setEditName(pkg.name);
    setEditUnits(String(pkg.units));
    setEditFeatures(pkg.features.join('\n'));
    setEditHighlighted(pkg.highlighted);
    setEditHighlightLabel(pkg.highlightLabel || '');
    setEditSpecial(pkg.special || false);
    // Neue Preisstruktur
    const basePrice = pkg.basePriceCents ?? (pkg.totalCentsImmediate ?? pkg.immediatePriceCents ?? 0);
    setEditBasePriceCents(String(basePrice / 100));
    setEditImmediateDiscountPercent(pkg.immediateDiscountPercent !== null && pkg.immediateDiscountPercent !== undefined ? String(pkg.immediateDiscountPercent) : '');
    setEditInstallmentEnabled(pkg.installmentEnabled ?? false);
    setEditInstallmentMonths(pkg.installmentMonths !== null ? String(pkg.installmentMonths) : '');
    setEditDefaultPaymentType(pkg.defaultPaymentType ?? 'immediate');
    setEditSignupFeeMode(pkg.signupFeeMode ?? 'never');
    setEditSignupFeeIncluded(pkg.signupFeeIncluded ?? false);
    setEditModalOpen(true);
  };

  const handleEditSave = async () => {
    if (!editingPackage) return;

    if (!editName.trim()) {
      toast.error('Paketname darf nicht leer sein');
      return;
    }
    const unitsNum = Number(editUnits);
    if (!Number.isFinite(unitsNum) || unitsNum <= 0) {
      toast.error('Anzahl Unterrichtseinheiten muss > 0 sein');
      return;
    }

    const basePriceNum = Number(editBasePriceCents.replace(',', '.')) * 100;
    if (!Number.isFinite(basePriceNum) || basePriceNum <= 0) {
      toast.error('Basispreis muss > 0 sein');
      return;
    }

    const discountPercentNum = editImmediateDiscountPercent.trim() !== '' ? Number(editImmediateDiscountPercent.replace(',', '.')) : null;
    if (discountPercentNum !== null && (!Number.isFinite(discountPercentNum) || discountPercentNum < 0 || discountPercentNum > 100)) {
      toast.error('Sofortzahlungsrabatt muss zwischen 0 und 100% sein');
      return;
    }

    const parsedFeatures = editFeatures
      .split('\n')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    const installmentMonthsNum = editInstallmentEnabled && editInstallmentMonths.trim() !== '' ? Number(editInstallmentMonths) : null;
    if (editInstallmentEnabled && (installmentMonthsNum === null || !Number.isFinite(installmentMonthsNum) || installmentMonthsNum <= 0)) {
      toast.error('Bei aktivierter Ratenzahlung muss eine Ratenanzahl angegeben werden');
      return;
    }

    // Wenn highlighted aktiviert wird, alle anderen deaktivieren
    const updatedPackages = packages.map((p) => {
      if (p.id === editingPackage.id) {
        const preisGeaendert =
          Math.round(basePriceNum) !== basisVon(p) || discountPercentNum !== rabattVon(p);
        return {
          ...(preisGeaendert ? ohneAbgeleitetePreise(p) : p),
          name: editName.trim(),
          units: unitsNum,
          features: parsedFeatures,
          highlighted: editHighlighted,
          highlightLabel: editHighlightLabel.trim() || null,
          special: editSpecial,
          basePriceCents: Math.round(basePriceNum),
          immediateDiscountPercent: discountPercentNum,
          installmentEnabled: editInstallmentEnabled,
          installmentMonths: installmentMonthsNum,
          defaultPaymentType: editInstallmentEnabled && installmentMonthsNum ? editDefaultPaymentType : 'immediate',
          signupFeeMode: editSignupFeeMode,
          signupFeeIncluded: editSignupFeeIncluded,
        };
      }
      // Wenn highlighted aktiviert wird, alle anderen deaktivieren
      if (editHighlighted) {
        return { ...p, highlighted: false };
      }
      return p;
    });

    setPackages(updatedPackages);
    const success = await savePackages(updatedPackages);
    if (success) {
      toast.success('Paket gespeichert');
      setEditModalOpen(false);
      resetEditForm();
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setPackages((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const reordered = arrayMove(items, oldIndex, newIndex);
        // Auto-save after reorder
        savePackages(reordered).then((success) => {
          if (success) {
            toast.success('Reihenfolge gespeichert');
          }
        });
        return reordered;
      });
    }
  };

  const handleMerge = async () => {
    if (!sourceUnterrichtsformId) {
      toast.error('Bitte Quell-Unterrichtsform auswählen');
      return;
    }

    try {
      await api.post(`/api/unterrichtsformen/${unterrichtsformId}/packages/merge-from`, {
        sourceUnterrichtsformId: Number(sourceUnterrichtsformId),
      });
      toast.success('Pakete erfolgreich übernommen');
      setMergeDialogOpen(false);
      setSourceUnterrichtsformId('');
      await loadPackages();
    } catch (e: any) {
      toast.error(e?.message || 'Fehler beim Übernehmen der Pakete');
    }
  };

  const formatPrice = (cents: number) => {
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Paketverwaltung</h2>
          <p className="text-muted-foreground">
            Pakete für diese Unterrichtsform definieren, bearbeiten und sortieren
          </p>
        </div>
        {onClose && (
          <Button variant="outline" onClick={onClose}>
            Schließen
          </Button>
        )}
      </div>

      <div className="flex gap-2">
        <Button onClick={() => setMergeDialogOpen(true)} variant="outline">
          <Copy className="mr-2 h-4 w-4" />
          Pakete aus anderer Unterrichtsform übernehmen
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Plus className="mr-2 h-5 w-5" />
            Neues Paket anlegen
          </CardTitle>
          <CardDescription>Definieren Sie die Grunddaten des Pakets.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="pkg-name">Name</Label>
              <Input
                id="pkg-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="z. B. 10er Karte"
              />
            </div>
            <div>
              <Label htmlFor="pkg-units">Anzahl Unterrichtseinheiten</Label>
              <Input
                id="pkg-units"
                type="number"
                min="1"
                value={units}
                onChange={(e) => setUnits(e.target.value)}
                placeholder="z. B. 10"
              />
            </div>
            
            {/* Preisbehandlung */}
            <div className="md:col-span-2 border-t pt-4">
              <h3 className="font-semibold mb-2">Preisbehandlung</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="base-price">Basispreis (€)</Label>
                  <Input
                    id="base-price"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={basePriceCents}
                    onChange={(e) => setBasePriceCents(e.target.value)}
                    placeholder="0.00"
                  />
                  {units && basePriceCents && Number(units) > 0 && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Pro UE: {formatPrice(Math.round(Number(basePriceCents.replace(',', '.')) * 100 / Number(units)))}
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="immediate-discount">Sofortzahlungsrabatt (%) - optional</Label>
                  <Input
                    id="immediate-discount"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={immediateDiscountPercent}
                    onChange={(e) => setImmediateDiscountPercent(e.target.value)}
                    placeholder="z. B. 5"
                  />
                  {basePriceCents && immediateDiscountPercent && Number(immediateDiscountPercent) > 0 && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Sofortzahlungspreis: {formatPrice(Math.round(Number(basePriceCents.replace(',', '.')) * 100 * (1 - Number(immediateDiscountPercent) / 100)))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Ratenzahlung */}
            <div className="md:col-span-2 border-t pt-4">
              <h3 className="font-semibold mb-2">Ratenzahlung</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="flex items-center gap-2">
                  <Switch
                    id="installment-enabled"
                    checked={installmentEnabled}
                    onCheckedChange={setInstallmentEnabled}
                  />
                  <Label htmlFor="installment-enabled">Ratenzahlung aktivieren</Label>
                </div>
                {installmentEnabled && (
                  <>
                    <div>
                      <Label htmlFor="installment-months">Anzahl Raten</Label>
                      <Input
                        id="installment-months"
                        type="number"
                        min="1"
                        value={installmentMonths}
                        onChange={(e) => setInstallmentMonths(e.target.value)}
                        placeholder="z. B. 12"
                      />
                      {installmentMonths && basePriceCents && Number(installmentMonths) > 0 && (
                        <div className="text-xs text-muted-foreground mt-1">
                          Pro Monat: {formatPrice(Math.round(Number(basePriceCents.replace(',', '.')) * 100 / Number(installmentMonths)))}
                        </div>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="default-payment-type">Standard-Zahlungsoption</Label>
                      <Select value={defaultPaymentType} onValueChange={(v: 'immediate' | 'installment') => setDefaultPaymentType(v)}>
                        <SelectTrigger id="default-payment-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="immediate">Sofortzahlung</SelectItem>
                          <SelectItem value="installment">Ratenzahlung</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Aufnahmegebühr */}
            <div className="md:col-span-2 border-t pt-4">
              <h3 className="font-semibold mb-2">Aufnahmegebühr</h3>
              <div>
                <Label htmlFor="signup-fee-mode">Wann wird die Aufnahmegebühr fällig?</Label>
                <Select value={signupFeeMode} onValueChange={(v: 'always' | 'never' | 'first_abo') => setSignupFeeMode(v)}>
                  <SelectTrigger id="signup-fee-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="never">Nie</SelectItem>
                    <SelectItem value="always">Immer</SelectItem>
                    <SelectItem value="first_abo">Nur beim ersten Abo</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-xs text-muted-foreground mt-1">
                  {signupFeeMode === 'first_abo' && 'Die Aufnahmegebühr wird nur berechnet, wenn der Kunde noch kein aktives Abo hat.'}
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <Switch
                  id="signup-fee-included"
                  checked={signupFeeIncluded}
                  onCheckedChange={setSignupFeeIncluded}
                />
                <Label htmlFor="signup-fee-included">Preis inkl. Anmeldegebühr anzeigen</Label>
              </div>
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="pkg-features">Features (eine pro Zeile)</Label>
              <Textarea
                id="pkg-features"
                value={features}
                onChange={(e) => setFeatures(e.target.value)}
                placeholder="z. B.&#10;Präsenz- und Onlineunterricht&#10;Notenmaterial-Flatrate&#10;wöchentliche Terminserie"
                rows={4}
              />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  id="pkg-special"
                  checked={special}
                  onCheckedChange={setSpecial}
                />
                <Label htmlFor="pkg-special">Special-Paket</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="pkg-highlighted"
                  checked={highlighted}
                  onCheckedChange={setHighlighted}
                />
                <Label htmlFor="pkg-highlighted">Hervorheben</Label>
              </div>
              {highlighted && (
                <div className="flex-1">
                  <Input
                    value={highlightLabel}
                    onChange={(e) => setHighlightLabel(e.target.value)}
                    placeholder="Label (z.B. 'meistgebucht', leer = Standard)"
                  />
                </div>
              )}
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button onClick={handleAdd}>
              <Plus className="mr-2 h-4 w-4" />
              Paket hinzufügen
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Definierte Pakete</CardTitle>
          <CardDescription>
            Verwalten Sie vorhandene Pakete. Ziehen Sie die Pakete per Drag & Drop, um die Reihenfolge zu ändern.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {packages.length === 0 ? (
            <p className="text-sm text-muted-foreground">Noch keine Pakete definiert.</p>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={packages.map((p) => p.id)} strategy={verticalListSortingStrategy}>
                <div className="overflow-x-auto">
                  <table className="w-full table-auto">
                    <thead>
                      <tr className="border-b text-left text-sm text-muted-foreground">
                        <th className="pb-3 pr-4 font-medium">Name</th>
                        <th className="pb-3 px-4 font-medium">Einheiten</th>
                        <th className="pb-3 px-4 font-medium">Preise</th>
                        <th className="pb-3 px-4 font-medium">Raten (Monate)</th>
                        <th className="pb-3 px-4 font-medium">Aufnahmegebühr</th>
                        <th className="pb-3 px-4 font-medium">Features</th>
                        <th className="pb-3 px-4 font-medium">Hervorhebung</th>
                        <th className="pb-3 pl-4 font-medium">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {packages.map((pkg) => (
                        <SortablePackageRow
                          key={pkg.id}
                          pkg={pkg}
                          onEdit={() => handleEditClick(pkg)}
                          onDelete={() => handleDelete(pkg.id)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </SortableContext>
            </DndContext>
          )}
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Paket bearbeiten</DialogTitle>
            <DialogDescription>
              Bearbeiten Sie die Details des Pakets "{editingPackage?.name}".
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 py-4">
            <div>
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Paketname"
              />
            </div>
            <div>
              <Label htmlFor="edit-units">Unterrichtseinheiten</Label>
              <Input
                id="edit-units"
                type="number"
                min="1"
                value={editUnits}
                onChange={(e) => setEditUnits(e.target.value)}
                placeholder="Einheiten"
              />
            </div>
            
            {/* Preisbehandlung Edit */}
            <div className="md:col-span-2 border-t pt-4">
              <h3 className="font-semibold mb-2">Preisbehandlung</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="edit-base-price">Basispreis (€)</Label>
                  <Input
                    id="edit-base-price"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={editBasePriceCents}
                    onChange={(e) => setEditBasePriceCents(e.target.value)}
                    placeholder="0.00"
                  />
                  {editUnits && editBasePriceCents && Number(editUnits) > 0 && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Pro UE: {formatPrice(Math.round(Number(editBasePriceCents.replace(',', '.')) * 100 / Number(editUnits)))}
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="edit-immediate-discount">Sofortzahlungsrabatt (%) - optional</Label>
                  <Input
                    id="edit-immediate-discount"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={editImmediateDiscountPercent}
                    onChange={(e) => setEditImmediateDiscountPercent(e.target.value)}
                    placeholder="z. B. 5"
                  />
                  {editBasePriceCents && editImmediateDiscountPercent && Number(editImmediateDiscountPercent) > 0 && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Sofortzahlungspreis: {formatPrice(Math.round(Number(editBasePriceCents.replace(',', '.')) * 100 * (1 - Number(editImmediateDiscountPercent) / 100)))}
                    </div>
                  )}
                  {editingPackage && abweichenderSofortpreis(editingPackage) !== null && (
                    <p role="note" className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
                      Gespeicherter Sofortpreis: {formatPrice(abweichenderSofortpreis(editingPackage) ?? 0)} — passt nicht zu Basispreis
                      und Rabatt. Er bleibt, solange beides unverändert ist. Neu berechnen: Basispreis oder Rabatt ändern
                      (z. B. Rabatt 0 eintragen).
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Ratenzahlung Edit */}
            <div className="md:col-span-2 border-t pt-4">
              <h3 className="font-semibold mb-2">Ratenzahlung</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="flex items-center gap-2">
                  <Switch
                    id="edit-installment-enabled"
                    checked={editInstallmentEnabled}
                    onCheckedChange={setEditInstallmentEnabled}
                  />
                  <Label htmlFor="edit-installment-enabled">Ratenzahlung aktivieren</Label>
                </div>
                {editInstallmentEnabled && (
                  <>
                    <div>
                      <Label htmlFor="edit-installment-months">Anzahl Raten</Label>
                      <Input
                        id="edit-installment-months"
                        type="number"
                        min="1"
                        value={editInstallmentMonths}
                        onChange={(e) => setEditInstallmentMonths(e.target.value)}
                        placeholder="z. B. 12"
                      />
                      {editInstallmentMonths && editBasePriceCents && Number(editInstallmentMonths) > 0 && (
                        <div className="text-xs text-muted-foreground mt-1">
                          Pro Monat: {formatPrice(Math.round(Number(editBasePriceCents.replace(',', '.')) * 100 / Number(editInstallmentMonths)))}
                        </div>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="edit-default-payment-type">Standard-Zahlungsoption</Label>
                      <Select value={editDefaultPaymentType} onValueChange={(v: 'immediate' | 'installment') => setEditDefaultPaymentType(v)}>
                        <SelectTrigger id="edit-default-payment-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="immediate">Sofortzahlung</SelectItem>
                          <SelectItem value="installment">Ratenzahlung</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Aufnahmegebühr Edit */}
            <div className="md:col-span-2 border-t pt-4">
              <h3 className="font-semibold mb-2">Aufnahmegebühr</h3>
              <div>
                <Label htmlFor="edit-signup-fee-mode">Wann wird die Aufnahmegebühr fällig?</Label>
                <Select value={editSignupFeeMode} onValueChange={(v: 'always' | 'never' | 'first_abo') => setEditSignupFeeMode(v)}>
                  <SelectTrigger id="edit-signup-fee-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="never">Nie</SelectItem>
                    <SelectItem value="always">Immer</SelectItem>
                    <SelectItem value="first_abo">Nur beim ersten Abo</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-xs text-muted-foreground mt-1">
                  {editSignupFeeMode === 'first_abo' && 'Die Aufnahmegebühr wird nur berechnet, wenn der Kunde noch kein aktives Abo hat.'}
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <Switch
                  id="edit-signup-fee-included"
                  checked={editSignupFeeIncluded}
                  onCheckedChange={setEditSignupFeeIncluded}
                />
                <Label htmlFor="edit-signup-fee-included">Preis inkl. Anmeldegebühr anzeigen</Label>
              </div>
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="edit-features">Features (eine pro Zeile)</Label>
              <Textarea
                id="edit-features"
                value={editFeatures}
                onChange={(e) => setEditFeatures(e.target.value)}
                placeholder="z. B.&#10;Präsenz- und Onlineunterricht&#10;Notenmaterial-Flatrate&#10;wöchentliche Terminserie"
                rows={6}
              />
            </div>
            <div className="md:col-span-2 flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  id="edit-special"
                  checked={editSpecial}
                  onCheckedChange={setEditSpecial}
                />
                <Label htmlFor="edit-special">Special-Paket</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="edit-highlighted"
                  checked={editHighlighted}
                  onCheckedChange={setEditHighlighted}
                />
                <Label htmlFor="edit-highlighted">Hervorheben</Label>
              </div>
              {editHighlighted && (
                <div className="flex-1">
                  <Input
                    value={editHighlightLabel}
                    onChange={(e) => setEditHighlightLabel(e.target.value)}
                    placeholder="Label (z.B. 'meistgebucht', leer = Standard)"
                  />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setEditModalOpen(false);
              resetEditForm();
            }}>
              Abbrechen
            </Button>
            <Button onClick={handleEditSave}>
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Dialog */}
      <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pakete übernehmen</DialogTitle>
            <DialogDescription>
              Wählen Sie eine Unterrichtsform aus, von der Pakete übernommen werden sollen. 
              Bestehende Pakete bleiben erhalten, passende werden aktualisiert, neue werden ergänzt.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="source-form">Quell-Unterrichtsform</Label>
            <Select value={sourceUnterrichtsformId} onValueChange={setSourceUnterrichtsformId}>
              <SelectTrigger id="source-form">
                <SelectValue placeholder="Unterrichtsform auswählen" />
              </SelectTrigger>
              <SelectContent>
                {availableForms.map((form) => (
                  <SelectItem key={form.id} value={String(form.id)}>
                    {form.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setMergeDialogOpen(false);
              setSourceUnterrichtsformId('');
            }}>
              Abbrechen
            </Button>
            <Button onClick={handleMerge} disabled={!sourceUnterrichtsformId}>
              Übernehmen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
